import * as React from "react";
import styled, { css } from "styled-components";
import differenceInDays from "date-fns/difference_in_days";

import { feedAvailable } from "../../lib/i18n";
import { colors, media } from "../../common/utils/style";
import WithLocale from "../../common/components/WithLocale";
import Translate from "../../common/components/Translate";
import Link from "../../common/components/router/Link";

export type NavFullPathCompany = WtdModels.Partial<WtdModels.Company, "company_name">;

export const navFullPath = (company: NavFullPathCompany, subpath: string): string => {
  return `/companies/${company.company_name}${subpath}`;
};

type NavItemCompany = WtdModels.Union<WtdModels.Partial<WtdModels.Company, "font_color_code">, NavFullPathCompany>;

type Location = "post_articles" | "companies";

type Company = WtdModels.Union<
  WtdModels.Partial<
    WtdModels.Company,
    "is_international_feed" | "lastest_post_published_at" | "post_count" | "project_count" | "has_descriptions"
  >,
  NavItemCompany
>;

export interface HeaderNavModels {
  Company: Company;
}

const ACTIVE_CLASS_NAME = "active-nav-link-item";

const NavItem: React.SFC<{
  to: string;
  company: NavItemCompany;
  onClick: any;
  disabled?: boolean;
  showBadge?: boolean;
  active?: boolean;
  location?: Location;
}> = props => {
  const { company, to, onClick, children, disabled, showBadge, active, location } = props;
  const activeColor = company.font_color_code === colors.gray2 ? colors.gray3 : company.font_color_code;
  const sharedProps = {
    className: active ? ACTIVE_CLASS_NAME : "",
    activeClassName: ACTIVE_CLASS_NAME,
    color: activeColor,
    disabled,
    onClick,
  };

  return location && location === "companies" ? (
    <NavLinkItem to={navFullPath(company, to)} {...sharedProps}>
      {showBadge && <Badge color={activeColor} />}
      {children}
    </NavLinkItem>
  ) : (
    <NavAnchorItem href={navFullPath(company, to)} {...sharedProps}>
      {showBadge && <Badge color={activeColor} />}
      {children}
    </NavAnchorItem>
  );
};

interface Props {
  company: Company;
  isMember?: boolean;
  location?: Location;
  onClickLink?(event: React.MouseEvent<HTMLLinkElement>): void;
  innerRef?(el: HTMLElement): void;
}

const defaultProps: Partial<Props> = {
  location: "companies",
};

const HeaderNav: React.SFC<Props> = props => {
  const { company, isMember, onClickLink, innerRef, location } = props;

  return (
    <Nav innerRef={innerRef}>
      <NavItem to="" company={company} onClick={onClickLink} location={location}>
        <Translate id="company.nav.home" />
      </NavItem>
      <NavItem
        to="/about"
        company={company}
        onClick={onClickLink}
        disabled={location !== "post_articles" && !isMember && !company.has_descriptions}
        location={location}
      >
        <Translate id="company.nav.about" />
      </NavItem>
      <NavItem to="/members" company={company} onClick={onClickLink} location={location}>
        <Translate id="company.nav.members" />
      </NavItem>
      <WithLocale>
        {({ country }) =>
          feedAvailable(country) || company.is_international_feed ? (
            <NavItem
              to="/feed"
              company={company}
              onClick={onClickLink}
              disabled={!isMember && company.post_count <= 0}
              showBadge={
                !!company.lastest_post_published_at &&
                differenceInDays(new Date(), company.lastest_post_published_at) <= 5
              }
              active={location === "post_articles"}
              location={location}
            >
              <Translate id="company.nav.posts" />
            </NavItem>
          ) : null
        }
      </WithLocale>
      <NavItem
        to="/projects"
        company={company}
        onClick={onClickLink}
        disabled={!isMember && company.project_count <= 0}
        location={location}
      >
        <Translate id="company.nav.projects" />
      </NavItem>
    </Nav>
  );
};

HeaderNav.defaultProps = defaultProps;

const Nav = styled.nav`
  display: inline-flex;
  padding-bottom: 1px; /* Avoid underbar of active item from being hidden */
`;

interface StyledProps {
  color: string;
  activeClassName?: string;
  disabled?: boolean;
  active?: boolean;
}

const NavLinkItem = styled(Link)`
  display: inline-block;
  flex-shrink: 0;
  position: relative;
  font-weight: 500;
  margin-right: 18px;
  padding: 13px 0;
  color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray1)};
  cursor: ${props => (props.disabled ? "default" : "pointer")};
  &:link,
  &:visited {
    color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray1)};
  }
  &:last-child {
    margin-right: 0;
  }
  &:hover {
    text-decoration: none;
    opacity: ${props => (props.disabled ? 1.0 : 0.5)};
  }
  @media ${media.large} {
    padding: 8px 0;
    color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray4)};
    transition: opacity 100ms;
    &:link,
    &:visited {
      color: ${props => (props.disabled ? css`rgba(0, 0, 0, 0.12)` : colors.gray4)};
    }
  }
  &.${(props: StyledProps) => props.activeClassName!} {
    font-weight: 600;
    color: ${(props: StyledProps) => props.color};
    &:before {
      position: absolute;
      width: 18px;
      height: 2px;
      border-radius: 1px;
      background-color: ${(props: StyledProps) => props.color};
      content: "";
      display: inline-block;
      bottom: -1px;
      left: 50%;
      transform: translateX(-50%);
    }
    @media ${media.large} {
      color: inherit;
      &:hover {
        opacity: 1;
      }
    }
  }
`;

const NavAnchorItem = NavLinkItem.withComponent("a");

const Badge = styled.span`
  position: absolute;
  top: 3px;
  left: -6px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(props: { color: string }) => props.color};
`;

export default HeaderNav;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/HeaderNav.tsx